import { Box, styled, Tooltip, Typography, useTheme } from '@mui/material'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { BeeCrewGrey } from '../../styles/theme-configuration'
import { environment } from '../../enviroments/enviroment'

export const StyledButtonTile = styled(Box)(({ theme }) => ({
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  gap: theme.spacing(2),
  borderRadius: theme.spacing(1),
  borderLeft: 'solid 8px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  color: 'black',
  width: '100%',
  textTransform: 'none',
  cursor: 'pointer',
  transition: 'all 150ms ease-in-out',
  '& svg': {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
}))

type NavigationTileProps = {
  icon: ReactNode
  title: string
  subtitle: string
  navigateTo: string
  index: number
  selectedIndex: number
  setSelectedIndex: Dispatch<SetStateAction<number>>
}

const NavigationTile = ({
  icon,
  title,
  subtitle,
  navigateTo,
  index,
  selectedIndex,
  setSelectedIndex,
}: NavigationTileProps) => {
  const navigate = useNavigate()
  const theme = useTheme()

  const handleClick = () => {
    let path = window.location.pathname.replace(environment.publicUrl, '')

    if (path !== navigateTo) {
      setSelectedIndex(index)
      navigate(navigateTo)
    }
  }

  return (
    <Tooltip
      title={`${title} ${subtitle}`}
      PopperProps={{
        sx: {
          opacity: { sm: '100%', md: '0%' },
        },
      }}
    >
      <StyledButtonTile
        tabIndex={0}
        className={selectedIndex ? 'selected' : ''}
        sx={{
          width: { xs: '100%' },
          paddingLeft: { sx: 0, md: theme.spacing(3) },
          paddingRight: { sx: 0, md: theme.spacing(4) },
          minWidth: 40,
          height: '100%',
          overflowX: 'hidden',
          color: index === selectedIndex ? BeeCrewGrey[950] : 'text.highEmphasis',
          justifyContent: { xs: 'center', md: 'flex-start' },
          borderColor: 'primary.dark',
          bgcolor: index === selectedIndex ? 'primary.main' : 'background.elevation1',
          ':hover': {
            bgcolor: index === selectedIndex ? 'primary.lightHover' : 'background.elevation2',
          },
          ':focus': {
            bgcolor: index === selectedIndex ? 'primary.lightHover' : 'background.elevation2',
          },
        }}
        onClick={() => handleClick()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick()
          }
        }}
      >
        {icon}
        <Box
          sx={{
            visibility: { sm: 'collapse', md: 'visible' },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'stretch',
            flex: 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            variant="h5"
            component="span"
            fontWeight={'700'}
            noWrap
            sx={{
              width: '100%',
              textAlign: 'left',
              textOverflow: 'ellipsis', // Add ellipsis for overflowed text
              whiteSpace: 'nowrap', // Prevent text from wrapping
              overflow: 'hidden',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            noWrap
            sx={{
              width: '100%',
              textAlign: 'left',
              textOverflow: 'ellipsis', // Add ellipsis for overflowed text
              whiteSpace: 'nowrap', // Prevent text from wrapping
              overflow: 'hidden',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </StyledButtonTile>
    </Tooltip>
  )
}

export default NavigationTile
